import { useEffect, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import PageHeader from '../../../components/common/PageHeader';
import { Button } from '../../../components/ui/button';
import { DataTable, DataTableColumn } from '../../../components/ui/data-table';
import StatusChip from '../../../components/common/StatusChip';
import ErrorState from '../../../components/common/ErrorState';
import LoadingSkeleton from '../../../components/common/LoadingSkeleton';
import { mamAuditService } from '../services/mamService';
import { formatDate } from '../utils';
import type { MAMAuditLog } from '../types';

export default function MAMAuditLogs() {
  const [logs, setLogs] = useState<MAMAuditLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadLogs = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await mamAuditService.getAll();
      setLogs(data);
    } catch {
      setError('Failed to fetch MAM audit logs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLogs();
  }, []);

  const columns: DataTableColumn<MAMAuditLog>[] = [
    {
      id: 'action',
      header: 'Action',
      sortable: true,
      accessor: log => (
        <span className="font-mono font-bold text-xs uppercase px-2 py-0.5 bg-muted rounded border border-border">
          {log.action}
        </span>
      ),
    },
    {
      id: 'description',
      header: 'Description',
      sortable: true,
      accessor: log => (
        <div>
          <p className="font-medium text-foreground text-xs">
            {log.description}
          </p>
          <p className="text-[10px] text-muted-foreground">
            Entity: {log.entity} (#{log.entityId})
          </p>
        </div>
      ),
    },
    {
      id: 'performedBy',
      header: 'Performed By',
      sortable: true,
      accessor: log => (
        <div>
          <p className="font-semibold text-foreground text-xs">
            {log.performedBy}
          </p>
          <p className="text-[10px] text-muted-foreground font-mono">
            IP: {log.ipAddress}
          </p>
        </div>
      ),
    },
    {
      id: 'timestamp',
      header: 'Timestamp',
      sortable: true,
      accessor: log => (
        <span className="text-[10px] text-muted-foreground">
          {formatDate(log.timestamp)}
        </span>
      ),
    },
    {
      id: 'status',
      header: 'Status',
      sortable: true,
      align: 'center',
      accessor: log => <StatusChip status={log.status} size="sm" />,
    },
  ];

  if (loading) {
    return (
      <div className="space-y-6">
        <PageHeader title="MAM Audit Logs" />
        <LoadingSkeleton type="table" rows={6} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
        <PageHeader title="MAM Audit Logs" />
        <ErrorState message={error} onRetry={loadLogs} />
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-6 w-full">
      <PageHeader
        title="MAM System Audit Trail"
        description="System activity, allocation rule changes, and trade execution events log"
      >
        <Button variant="outline" size="sm" onClick={loadLogs}>
          <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Refresh
        </Button>
      </PageHeader>

      <DataTable
        columns={columns}
        data={logs}
        searchKeys={['action', 'description', 'performedBy', 'entity']}
        searchPlaceholder="Search audit logs by action, description, or user..."
        initialPageSize={8}
      />
    </div>
  );
}
